import { z } from 'zod'

export const agentConfigSchema = z.object({
  // Personalidade - Comportamento
  systemPrompt: z
    .string()
    .min(10, 'O prompt do sistema deve ter pelo menos 10 caracteres'),

  // Personalidade - Configurações
  name: z.string().min(1, 'Nome é obrigatório'),
  initialMessage: z.string().min(1, 'Mensagem inicial é obrigatória'),
  tone: z.enum(['practical', 'formal', 'casual', 'funny']),
  model: z.enum(['gpt-4o', 'gpt-4o-mini']),
  language: z.string(),
  timezone: z.string(),
  responseDelay: z.number().min(0).max(300),

  // Personalidade - Perfil da Empresa
  companyName: z.string().optional(),
  companySector: z.string().optional(),
  companyWebsite: z.string().url().optional().or(z.literal('')),
  companyDescription: z.string().optional(),

  // Cérebro - Base de conhecimento
  files: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        size: z.number(),
        url: z.string(),
      })
    )
    .optional(),
  websites: z.array(z.string().url()).optional(),
  youtubeLinks: z.array(z.string().url()).optional(),
  qaPairs: z
    .array(
      z.object({
        question: z.string(),
        answer: z.string(),
      })
    )
    .optional(),

  // Habilidades
  isActive: z.boolean(),
  canRespondAudio: z.boolean(),
  audioVoice: z.enum(['male', 'female']).optional(),

  // Estágios e Ações
  stages: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        instructions: z.string(),
        triggers: z.array(z.string()),
        actions: z
          .array(
            z.discriminatedUnion('type', [
              z.object({
                type: z.literal('finalizar_conversa'),
                id: z.string(),
              }),
              z.object({
                type: z.literal('transferir_atendimento'),
                id: z.string(),
                payload: z
                  .object({
                    targetType: z.enum(['attendant', 'department']),
                    targetId: z.string(),
                    targetName: z.string(),
                  })
                  .refine(
                    (data) => data.targetId && data.targetId.trim().length > 0,
                    {
                      message: 'Selecione um atendente ou departamento',
                      path: ['targetId'],
                    }
                  )
                  .refine(
                    (data) =>
                      data.targetName && data.targetName.trim().length > 0,
                    {
                      message: 'Nome do destino é obrigatório',
                      path: ['targetName'],
                    }
                  ),
              }),
              z.object({
                type: z.literal('aplicar_tags'),
                id: z.string(),
                payload: z
                  .object({
                    tagIds: z.array(z.string()),
                  })
                  .refine((data) => data.tagIds && data.tagIds.length > 0, {
                    message: 'Selecione pelo menos um segmento',
                    path: ['tagIds'],
                  }),
              }),
            ])
          )
          .optional(),
      })
    )
    .optional(),
})

export type AgentConfig = z.infer<typeof agentConfigSchema>

export const defaultAgentConfig: AgentConfig = {
  systemPrompt: '',
  name: '',
  initialMessage: 'Olá! Como posso ajudar você hoje?',
  tone: 'practical',
  model: 'gpt-4o-mini',
  language: 'auto',
  timezone: 'America/Sao_Paulo',
  responseDelay: 7,
  companyName: '',
  companySector: '',
  companyWebsite: '',
  companyDescription: '',
  files: [],
  websites: [],
  youtubeLinks: [],
  qaPairs: [],
  isActive: true,
  canRespondAudio: false,
  audioVoice: 'female',
  stages: [],
}

export type StageAction =
  | { type: 'finalizar_conversa'; id: string }
  | {
      type: 'transferir_atendimento'
      id: string
      payload: {
        targetType: 'attendant' | 'department'
        targetId: string
        targetName: string
      }
    }
  | { type: 'aplicar_tags'; id: string; payload: { tagIds: string[] } }

export type Stage = {
  id: string
  name: string
  instructions: string
  triggers: string[]
  actions?: StageAction[]
}
